import { Badge, Box, Center, Flex, Heading, Icon, Image, ListItem, Skeleton, Text, UnorderedList } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';
import { PiImageBroken } from 'react-icons/pi';
import { formattedCurrency } from '../../../helper/utils/formattedCurrency';
import { isEmptyObject } from '../../../helper/utils/isEmptyObject';
import useOnlineSales from '../hooks/useOnlineSales';
import ProductItemQty from './ProductItemQty';
import ProductItemQtyPackage from './ProductItemQtyPackage';

export default function ProductTickets({ isLoading = false, productData = {}, isProductRecurring = false, tickets = [], selectedData = {}, selectedItems = [], combo = [], onChangeQty = (item, qty) => {}, maxQty = 10, minQty = 1, misc = { pkg: {}, isMultiSelection: false } }) {
    const data = tickets.length > 0 ? tickets[0] : {};
    // Menjaga variabel tetap ada meski tidak digunakan di JSX (sesuai kode asli)
    const isNeedTimeslot = isEmptyObject(data) ? false : +(data['isNeedTimeSlot'] || '0') === 1;
    const isItemRecurring = isProductRecurring;
    const parentQtyItem = +(productData?.qty ?? 0);

    const renderMenuName = (item = {}) => {
        if (item?.menuType === 'packages') {
            return item?.menuPackageShortName === '' ? (item?.onlineName ?? item?.menuName ?? '') : item?.menuPackageShortName;
        }
        return (item?.onlineName ?? item?.menuName ?? '').toString().trim().toLowerCase();
    };

    const { roomPartyAvailability = {} } = useOnlineSales();

    return (
        <Box w='full'>
            {tickets?.map((item = {}, index) => {
                const image = (item?.imageUrl ?? '').toString().trim();
                const price = item?.basePrice ?? 0.0;
                const isDefaultMenu = Number(item?.isDefaultMenu) === 1;
                const qtyMinDefault = !isEmptyObject(misc?.pkg || {}) ? (isDefaultMenu ? minQty * parentQtyItem : 0) : minQty;
                const qtyMaxDefault = maxQty;
                const menuType = item?.menuType ?? '';
                const isMenuParty = menuType === 'party';
                const isMenuPkg = menuType === 'packages';
                const isMenuMembership = menuType === 'membership';

                return (
                    <Flex key={item['id'] || item['menuCode'] || index} w='full' h='max-content' bg='white' alignItems='center' justifyContent='start' gap={4} p={3} rounded='lg' mb={3}>
                        {/* Image Section */}
                        <Skeleton isLoaded={!isLoading} rounded='xl'>
                            <Box w={{ base: '125px', md: '175px' }} h={{ base: '75px', md: '100px' }}>
                                <Center w='full' h='full' bg={image !== '' ? 'transparent' : 'gray.50'} rounded='lg' overflow='hidden'>
                                    {image !== '' ? <Image src={image} w='full' h='full' loading='lazy' fit='contain' /> : <Icon fontSize='35px' color='gray.400' as={PiImageBroken} />}
                                </Center>
                            </Box>
                        </Skeleton>

                        {/* Content Section */}
                        <Box w='full'>
                            <Flex w='full' flexWrap={{ base: 'wrap', md: 'nowrap' }} justifyContent='space-between' alignItems={{ base: 'start', md: 'center' }}>
                                <Box w='full'>
                                    {isMenuMembership && (
                                        <Badge colorScheme='orange' fontSize='xs' rounded='md' p={1} px={2} mb={2}>
                                            Member Only
                                        </Badge>
                                    )}
                                    <Skeleton isLoaded={!isLoading} rounded='xl'>
                                        <Heading fontWeight={500} textTransform='uppercase' fontSize={{ base: '15px', md: 'lg' }}>
                                            {renderMenuName(item)}
                                        </Heading>
                                    </Skeleton>
                                    {!isMenuParty && (
                                        <Skeleton isLoaded={!isLoading} rounded='xl'>
                                            <Text mt={1} fontSize='13.5px' color='gray.500' casing='capitalize' noOfLines={isMenuPkg ? 999 : { base: 1, md: 2 }}>
                                                {(item['menuDescription'] || '').toString().trim().toLowerCase()}
                                            </Text>
                                        </Skeleton>
                                    )}
                                    <Skeleton isLoaded={!isLoading} rounded='xl'>
                                        <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight={600} mt={3}>
                                            {Number(price) === 0 ? '' : formattedCurrency(price)}
                                        </Text>
                                    </Skeleton>
                                </Box>
                                {(!isItemRecurring || isMenuMembership) && (
                                    <Box w='full' display='flex' alignItems='center' justifyContent='end'>
                                        <ItemQty isDefaultMenu={isDefaultMenu} minQty={qtyMinDefault} maxQty={qtyMaxDefault} isItemRecurring={isItemRecurring} isLoading={isLoading} onChangeQty={(qty) => onChangeQty({ ...item, qtyUsed: minQty }, qty)} item={item} selectedData={selectedData} selectedItems={selectedItems} misc={misc} />
                                    </Box>
                                )}
                            </Flex>

                            <RoomPartyAvailability roomPartyAvailability={roomPartyAvailability} item={item} />

                            {/* Combo Section */}
                            {combo.length > 0 && (
                                <Box mt={7}>
                                    <Skeleton isLoaded={!isLoading} rounded='xl'>
                                        <Heading fontWeight={700} fontSize={{ base: 'sm', md: 'md' }}>
                                            Includes :
                                        </Heading>
                                    </Skeleton>

                                    <Skeleton isLoaded={!isLoading} rounded='xl'>
                                        <Box mt={2}>
                                            <UnorderedList>
                                                {combo.map((comboItem, idx) => (
                                                    <ListItem key={idx} fontWeight={600} fontSize='sm'>
                                                        {comboItem['menuName'] || ''}
                                                    </ListItem>
                                                ))}
                                            </UnorderedList>
                                        </Box>
                                    </Skeleton>
                                </Box>
                            )}
                        </Box>
                    </Flex>
                );
            })}
        </Box>
    );
}

function ItemQty({ isLoading, item, selectedData, selectedItems, onChangeQty, isItemRecurring, minQty, maxQty, misc, isDefaultMenu }) {
    const max = isItemRecurring ? 1 : maxQty;

    const currentQty = useMemo(() => {
        if (selectedItems && selectedItems.length > 0) {
            const selectedItem = selectedItems.find((si) => {
                const menuPackageItemId = String(si?.menuPackageItemId ?? '');

                if (menuPackageItemId) {
                    return menuPackageItemId === String(item?.menuPackageItemId ?? '');
                }

                return si.menuCode === item.menuCode;
            });

            return selectedItem ? selectedItem.qty : 0;
        }
        return selectedData['qty'] || 0;
    }, [selectedItems, selectedData, item]);

    const hasAutoSelected = useRef(false);
    const disabledControlButton = isDefaultMenu && minQty === maxQty;

    useEffect(() => {
        if (!hasAutoSelected.current && isDefaultMenu && minQty > 0 && currentQty === 0) {
            hasAutoSelected.current = true;

            const timer = setTimeout(() => {
                onChangeQty(minQty);
            }, 200);

            return () => {
                hasAutoSelected.current = false;

                return clearTimeout(timer);
            };
        }
    }, [isDefaultMenu, minQty, currentQty]);

    if (isItemRecurring) return null;

    return (
        <Box mt={4} w='full'>
            <Skeleton isLoaded={!isLoading} rounded='xl'>
                {item?.menuType === 'packages' && misc?.isMultiSelection ? <ProductItemQtyPackage defaultValue={currentQty} max={max} min={minQty} canDelete={false} size='md' data={item} onChangeQty={onChangeQty} misc={misc} showControlQty={!disabledControlButton} /> : <ProductItemQty defaultValue={currentQty} max={max} min={minQty} canDelete={false} size='md' data={item} onChangeQty={onChangeQty} showControlQty={true} showInputQty={true} />}

                {minQty > 1 && !disabledControlButton && (
                    <Text fontSize='sm' mt={2} textAlign='center' color='red.500' fontWeight={600} fontStyle='italic'>
                        *Min quantity order : {minQty}
                    </Text>
                )}
            </Skeleton>
        </Box>
    );
}

const RoomPartyAvailability = ({ roomPartyAvailability, item }) => {
    if (!roomPartyAvailability) return null;

    return (
        <Flex w='full' alignItems='center' justifyContent='flex-end' mt={2} flexDirection={{ base: 'column', md: 'row' }}>
            {String(item?.isParentMenu) === '1' && roomPartyAvailability?.roomsRemaining > 0 ? (
                <Flex w={{ base: 'full', md: 'max-content' }} alignItems='center' gap={2} px={3} py={2} borderRadius='md' bg='red.50' border='1px solid' borderColor='red.200' flexDirection={{ base: 'column', md: 'row' }}>
                    <Badge colorScheme='red' variant='solid'>
                        Party Room
                    </Badge>
                    <Text fontSize='sm' color='red.700' fontWeight={600}>
                        {roomPartyAvailability?.roomsRemaining ?? 0} slot available
                    </Text>
                </Flex>
            ) : null}
        </Flex>
    );
};
